import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ShoppingCart, Plus, Minus, Trash2, Receipt, Wallet } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { rupiah } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";

const PAYMENTS = ["Tunai", "QRIS", "Transfer"];

export default function Kasir() {
  const qc = useQueryClient();
  const { data: recipes = [] } = useQuery({ queryKey: ["recipes"], queryFn: async () => (await api.get("/recipes")).data });
  const { data: sales = [] } = useQuery({ queryKey: ["sales"], queryFn: async () => (await api.get("/sales")).data });

  const [cart, setCart] = useState([]); // {id, name, price, hpp, qty}
  const [payment, setPayment] = useState("Tunai");
  const [received, setReceived] = useState("");
  const [processing, setProcessing] = useState(false);

  const total = useMemo(() => cart.reduce((s, c) => s + c.price * c.qty, 0), [cart]);
  const change = Number(received) - total;

  const addToCart = (r) => {
    setCart((c) => {
      const found = c.find((x) => x.id === r.id);
      if (found) return c.map((x) => (x.id === r.id ? { ...x, qty: x.qty + 1 } : x));
      return [...c, { id: r.id, name: r.name, price: r.computed.selling_price, hpp: r.computed.hpp_per_unit, qty: 1 }];
    });
  };
  const setQty = (id, d) => setCart((c) => c.map((x) => (x.id === id ? { ...x, qty: Math.max(1, x.qty + d) } : x)));
  const removeItem = (id) => setCart((c) => c.filter((x) => x.id !== id));

  const checkout = async () => {
    if (cart.length === 0) return toast.error("Keranjang masih kosong.");
    setProcessing(true);
    try {
      for (const item of cart) {
        await api.post("/sales", {
          recipe_id: item.id, product_name: item.name, quantity: item.qty,
          unit_price: item.price, hpp_per_unit: item.hpp, payment_method: payment,
        });
      }
      toast.success(`Transaksi berhasil! Total ${rupiah(total)}`);
      setCart([]); setReceived("");
      qc.invalidateQueries({ queryKey: ["sales"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
      qc.invalidateQueries({ queryKey: ["report-full"] });
      qc.invalidateQueries({ queryKey: ["report-mini"] });
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally { setProcessing(false); }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-heading text-2xl font-bold tracking-tight sm:text-3xl">Kasir (POS)</h1>
        <p className="text-sm text-muted-foreground">Pilih produk, catat penjualan, dan hitung kembalian.</p>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
        {/* Product catalog */}
        <div className="lg:col-span-7">
          {recipes.length === 0 ? (
            <Card className="rounded-2xl p-10 text-center text-muted-foreground">Belum ada produk. Buat resep di menu Bahan & Resep.</Card>
          ) : (
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
              {recipes.map((r) => (
                <button key={r.id} onClick={() => addToCart(r)} data-testid={`pos-product-item-${r.id}`}
                  className="flex flex-col items-start rounded-2xl border border-border bg-card p-4 text-left shadow-sm transition-all hover:border-primary hover:shadow-md active:scale-[0.98] min-h-[92px]">
                  <Badge variant="secondary" className="rounded-md text-[10px]">{r.category}</Badge>
                  <span className="mt-2 line-clamp-2 text-sm font-semibold leading-tight">{r.name}</span>
                  <span className="mt-auto pt-2 font-mono text-base font-bold text-primary">{rupiah(r.computed.selling_price)}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Cart */}
        <div className="lg:col-span-5">
          <Card className="sticky top-20 rounded-2xl p-5">
            <div className="flex items-center gap-2">
              <ShoppingCart className="h-5 w-5 text-primary" />
              <h3 className="font-heading text-lg font-semibold">Keranjang</h3>
              {cart.length > 0 && <Badge className="ml-auto rounded-full">{cart.length}</Badge>}
            </div>

            <div className="mt-4 space-y-2 max-h-64 overflow-y-auto">
              {cart.length === 0 && <p className="py-6 text-center text-sm text-muted-foreground">Keranjang kosong. Ketuk produk untuk menambah.</p>}
              {cart.map((item) => (
                <div key={item.id} className="flex items-center gap-2 rounded-xl bg-secondary/40 p-2.5" data-testid={`cart-item-${item.id}`}>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium">{item.name}</p>
                    <p className="font-mono text-xs text-muted-foreground">{rupiah(item.price)}</p>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <button onClick={() => setQty(item.id, -1)} className="flex h-7 w-7 items-center justify-center rounded-lg bg-card"><Minus className="h-3.5 w-3.5" /></button>
                    <span className="w-6 text-center font-mono text-sm font-semibold">{item.qty}</span>
                    <button onClick={() => setQty(item.id, 1)} className="flex h-7 w-7 items-center justify-center rounded-lg bg-card"><Plus className="h-3.5 w-3.5" /></button>
                    <button onClick={() => removeItem(item.id)} className="ml-1 text-muted-foreground hover:text-destructive"><Trash2 className="h-4 w-4" /></button>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-4 space-y-3 border-t border-border pt-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Total</span>
                <span className="font-mono text-2xl font-extrabold" data-testid="pos-total">{rupiah(total)}</span>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs">Metode Bayar</Label>
                  <Select value={payment} onValueChange={setPayment}>
                    <SelectTrigger className="rounded-xl" data-testid="pos-payment-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{PAYMENTS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Uang Diterima</Label>
                  <Input type="number" value={received} onChange={(e) => setReceived(e.target.value)} placeholder="Rp"
                    className="rounded-xl font-mono" data-testid="pos-received-input" />
                </div>
              </div>
              {received !== "" && Number(received) >= total && (
                <div className="flex items-center justify-between rounded-xl bg-primary/10 p-3">
                  <span className="text-sm font-medium text-primary">Kembalian</span>
                  <span className="font-mono text-lg font-bold text-primary" data-testid="pos-change">{rupiah(change)}</span>
                </div>
              )}
              <Button onClick={checkout} disabled={processing || cart.length === 0} data-testid="pos-checkout-button"
                className="w-full rounded-xl py-3 text-base min-h-[48px]">
                <Wallet className="mr-2 h-5 w-5" /> Proses Pembayaran
              </Button>
            </div>
          </Card>
        </div>
      </div>

      {/* Recent transactions */}
      <Card className="rounded-2xl p-5">
        <div className="mb-3 flex items-center gap-2">
          <Receipt className="h-5 w-5 text-muted-foreground" />
          <h3 className="font-heading text-lg font-semibold">Transaksi Terakhir</h3>
        </div>
        <div className="space-y-2">
          {sales.slice(0, 8).map((s) => (
            <div key={s.id} className="flex items-center justify-between rounded-xl border border-border px-4 py-2.5 text-sm" data-testid={`sale-row-${s.id}`}>
              <div>
                <p className="font-medium">{s.product_name} <span className="text-muted-foreground">×{s.quantity}</span></p>
                <p className="text-xs text-muted-foreground">{new Date(s.date).toLocaleString("id-ID")} · {s.payment_method}</p>
              </div>
              <span className="font-mono font-semibold">{rupiah(s.total)}</span>
            </div>
          ))}
          {sales.length === 0 && <p className="py-4 text-center text-sm text-muted-foreground">Belum ada transaksi.</p>}
        </div>
      </Card>
    </div>
  );
}
